/**
 * In-memory mock database — emula el estado completo del back.
 *
 * Mantiene state entre llamadas a los endpoints (igual que un servidor real).
 * Cuando Eze conecte el back, este módulo se reemplaza por consultas reales y
 * el resto del código (endpoints, stores, features) no cambia.
 */

import type { Usuario } from '@/api/contracts/usuarios';
import type { Ubicacion } from '@/api/contracts/ubicaciones';
import type { Producto } from '@/api/contracts/productos';
import type { Cliente } from '@/api/contracts/clientes';
import type { Serie } from '@/api/contracts/series';
import type { Remito } from '@/api/contracts/remitos';
import type { Operacion, Movimiento } from '@/api/contracts/operaciones';
import type { EventoAnmat } from '@/api/contracts/eventos-anmat';
import {
  SEED_USUARIOS,
  SEED_UBICACIONES,
  SEED_PRODUCTOS,
  SEED_CLIENTES,
  SEED_SERIES,
  SEED_REMITOS,
  SEED_OPERACIONES,
  SEED_MOVIMIENTOS,
  SEED_EVENTOS_ANMAT,
} from './seed';

interface MockDb {
  usuarios: Usuario[];
  ubicaciones: Ubicacion[];
  productos: Producto[];
  clientes: Cliente[];
  series: Serie[];
  remitos: Remito[];
  operaciones: Operacion[];
  movimientos: Movimiento[];
  eventosAnmat: EventoAnmat[];
  /** Counter incremental para los IDs de operación (OP-NNNNNN). */
  opCounter: number;
  /** Counter para movimientos (MOV-NNNNNN). */
  movCounter: number;
}

const db: MockDb = {
  usuarios: structuredClone(SEED_USUARIOS),
  ubicaciones: structuredClone(SEED_UBICACIONES),
  productos: structuredClone(SEED_PRODUCTOS),
  clientes: structuredClone(SEED_CLIENTES),
  series: structuredClone(SEED_SERIES),
  remitos: structuredClone(SEED_REMITOS),
  operaciones: structuredClone(SEED_OPERACIONES),
  movimientos: structuredClone(SEED_MOVIMIENTOS),
  eventosAnmat: structuredClone(SEED_EVENTOS_ANMAT),
  opCounter: 0,
  movCounter: 0,
};

export function getDb(): MockDb {
  return db;
}

/**
 * Resetea el mock a los valores iniciales del seed. Útil para tests y para
 * un eventual botón "Resetear demo" del Admin (en producción ese botón NO
 * existe — el reset destructivo es deuda documentada en GAMP 5).
 */
export function resetDb(): void {
  db.usuarios = structuredClone(SEED_USUARIOS);
  db.ubicaciones = structuredClone(SEED_UBICACIONES);
  db.productos = structuredClone(SEED_PRODUCTOS);
  db.clientes = structuredClone(SEED_CLIENTES);
  db.series = structuredClone(SEED_SERIES);
  db.remitos = structuredClone(SEED_REMITOS);
  db.operaciones = structuredClone(SEED_OPERACIONES);
  db.movimientos = structuredClone(SEED_MOVIMIENTOS);
  db.eventosAnmat = structuredClone(SEED_EVENTOS_ANMAT);
  db.opCounter = 0;
  db.movCounter = 0;
}

export function nextOpId(): string {
  db.opCounter += 1;
  return `OP-${String(db.opCounter).padStart(6, '0')}`;
}

export function nextMovId(): string {
  db.movCounter += 1;
  return `MOV-${String(db.movCounter).padStart(6, '0')}`;
}
